import { UserAPI } from "@shared/types";
import dayjs from "dayjs";
import i18n from "../lib/i18n";

/**
 * Date utility functions for handling dates without timezone conversion
 * Uses UTC methods to ensure consistent date display across timezones
 */

/**
 * Gets the current locale for date formatting based on i18n language
 * Maps language codes to locale codes (e.g., 'de' -> 'de-DE', 'en' -> 'en-US')
 */
export function getDateLocale(): string {
  const lang = i18n.language || 'en';
  const localeMap: Record<string, string> = {
    'en': 'en-US',
    'de': 'de-DE',
    'fr': 'fr-FR',
    'es': 'es-ES',
  };
  return localeMap[lang] || 'en-US';
}

export interface ParsedDate {
  year: number;
  month: string;
  monthShort: string;
  day: number;
  weekday: string;
  hours: number;
  minutes: number;
  formattedTime: string;
}

/**
 * Parses a date string or Date object and returns all components in UTC timezone
 * This prevents timezone conversion issues when displaying event dates
 * 
 * @param dateStr - ISO date string, Date object, or any valid date string
 * @returns Parsed date components in UTC
 */
export function parseUTCDate(dateStr: string | Date, locale?: string): ParsedDate {
  const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
  const dateLocale = locale || getDateLocale();
  
  const year = date.getUTCFullYear();
  const month = date.toLocaleDateString(dateLocale, { month: 'long', timeZone: 'UTC' });
  const monthShort = date.toLocaleDateString(dateLocale, { month: 'short', timeZone: 'UTC' });
  const day = date.getUTCDate();
  const weekday = date.toLocaleDateString(dateLocale, { weekday: 'long', timeZone: 'UTC' });
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  
  // Format time in 12-hour format with AM/PM
  const formattedTime = formatTime(hours, minutes);
  
  return {
    year,
    month,
    monthShort,
    day,
    weekday,
    hours,
    minutes,
    formattedTime,
  };
}

/**
 * Formats hours and minutes into 12-hour format with AM/PM
 * 
 * @param hours - Hours in 24-hour format (0-23)
 * @param minutes - Minutes (0-59)
 * @returns Formatted time string (e.g., "7:30 PM")
 */
export function formatTime(hours: number, minutes: number): string {
  const hour12 = hours % 12 || 12;
  const minutesStr = minutes.toString().padStart(2, '0');
  const period = hours >= 12 ? 'PM' : 'AM';
  
  return `${hour12}:${minutesStr} ${period}`;
}

/**
 * Formats a date string or Date object into a full date display
 * Example: "Monday, January 15, 2025"
 * 
 * @param dateStr - ISO date string, Date object, or any valid date string
 * @returns Formatted full date string
 */
export function formatFullDate(dateStr: string | Date): string {
  const { weekday, month, day, year } = parseUTCDate(dateStr);
  return `${weekday}, ${month} ${day}, ${year}`;
}

/**
 * Formats a date string or Date object into a short date display
 * Example: "Jan 15, 2025"
 * 
 * @param dateStr - ISO date string, Date object, or any valid date string
 * @returns Formatted short date string
 */
export function formatShortDate(dateStr: string | Date): string {
  const { monthShort, day, year } = parseUTCDate(dateStr);
  return `${monthShort} ${day}, ${year}`;
}

/**
 * Formats a date string or Date object into a short date with time
 * Example: "Jan 15, 2025 at 7:30 PM"
 * 
 * @param dateStr - ISO date string, Date object, or any valid date string
 * @returns Formatted date and time string
 */
export function formatShortDateTime(dateStr: string | Date): string {
  const { monthShort, day, year, formattedTime } = parseUTCDate(dateStr);
  return `${monthShort} ${day}, ${year} at ${formattedTime}`;
}

/**
 * Formats a date string to full date display (no timezone conversion)
 * Example: "2025-01-15" -> "Wednesday, January 15, 2025"
 * 
 * @param dateStr - Date string in format "YYYY-MM-DD"
 * @param locale - Optional locale override
 * @returns Formatted date string
 */
export function formatDateToFull(dateStr: string, locale?: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const dateLocale = locale || getDateLocale();
  
  return date.toLocaleDateString(dateLocale, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Formats a date string to short date display (no timezone conversion)
 * Example: "2025-01-15" -> "Jan 15, 2025"
 * 
 * @param dateStr - Date string in format "YYYY-MM-DD"
 * @param locale - Optional locale override
 * @returns Short formatted date string
 */
export function formatDateToShort(dateStr: string, locale?: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const dateLocale = locale || getDateLocale();
  
  return date.toLocaleDateString(dateLocale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Formats a time string to 12-hour format (no timezone conversion)
 * Example: "19:30:00" -> "7:30 PM"
 * 
 * @param timeStr - Time string in format "HH:MM:SS" or "HH:MM"
 * @returns Formatted time string in 12-hour format
 */
export function formatTimeToDisplay(timeStr: string): string {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return formatTime(hours, minutes);
}

/**
 * Combines date and time strings and formats them for display (no timezone conversion)
 * Example: ("2025-01-15", "19:30:00") -> "Jan 15, 2025 at 7:30 PM"
 * 
 * @param dateStr - Date string in format "YYYY-MM-DD"
 * @param timeStr - Time string in format "HH:MM:SS" or "HH:MM"
 * @returns Formatted date and time string
 */
export function formatDateTimeToDisplay(dateStr: string, timeStr: string): string {
  const dateFormatted = formatDateToShort(dateStr);
  const timeFormatted = formatTimeToDisplay(timeStr);
  
  return `${dateFormatted} at ${timeFormatted}`;
}

/**
 * Compact 12-hour time, dropping ":00" minutes.
 * Example: (15, 0) -> "3PM", (15, 30) -> "3:30PM"
 */
function formatCompactTime(hours: number, minutes: number): string {
  const hour12 = hours % 12 || 12;
  const period = hours >= 12 ? 'PM' : 'AM';
  return minutes === 0
    ? `${hour12}${period}`
    : `${hour12}:${minutes.toString().padStart(2, '0')}${period}`;
}

/** English ordinal suffix for a day-of-month (1 -> "st", 25 -> "th"). */
function ordinalSuffix(day: number): string {
  const ones = day % 10;
  const tens = day % 100;
  if (tens >= 11 && tens <= 13) return 'th';
  if (ones === 1) return 'st';
  if (ones === 2) return 'nd';
  if (ones === 3) return 'rd';
  return 'th';
}

/**
 * Short, contextual date+time label for event cards (no timezone conversion).
 * Within the next week the date is just the weekday; further out it adds the
 * month/day so it stays unambiguous. The time comes from the separate event
 * `time` field ("HH:MM" / "HH:MM:SS") since the date string is date-only.
 * Examples: "Tue - 3PM", "Tue, Dec 25th - 3PM"
 *
 * @param dateStr - Date string ("YYYY-MM-DD") or Date object
 * @param timeStr - Time string in "HH:MM" or "HH:MM:SS" format
 * @param locale - Optional locale override
 */
export function formatRelativeEventDate(
  dateStr: string | Date,
  timeStr?: string | null,
  locale?: string,
): string {
  const date = dateStr instanceof Date ? dateStr : new Date(dateStr);
  const dateLocale = locale || getDateLocale();
  const weekdayShort = date.toLocaleDateString(dateLocale, { weekday: 'short', timeZone: 'UTC' });
  const monthShort = date.toLocaleDateString(dateLocale, { month: 'short', timeZone: 'UTC' });
  const day = date.getUTCDate();

  const [h, m] = (timeStr ?? '').split(':').map(Number);
  const time = Number.isFinite(h) ? formatCompactTime(h, Number.isFinite(m) ? m : 0) : null;

  // Days between today and the event, compared as calendar days in UTC.
  const now = new Date();
  const eventDayUTC = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const daysAway = Math.round((eventDayUTC - todayUTC) / 86_400_000);

  let datePart: string;
  if (daysAway === 0) datePart = 'Today';
  else if (daysAway === 1) datePart = 'Tomorrow';
  else if (daysAway > 1 && daysAway < 7) datePart = weekdayShort;
  else datePart = `${weekdayShort}, ${monthShort} ${day}${ordinalSuffix(day)}`;
  return time ? `${datePart} - ${time}` : datePart;
}

export function eventTimeToDaysjs(eventTime: UserAPI.ReservationEventTime): dayjs.Dayjs {
  return todayjs(eventTime.date, eventTime.time);
}

export function todayjs(dateStr: string, timeStr?: string): dayjs.Dayjs {
  return dayjs(`${dateStr}${timeStr ? `T${timeStr}:00` : ''}`);
}

/**
 * Formats a Date object with locale-aware formatting
 * Uses the current i18n language to determine locale
 * 
 * @param date - Date object or date string to format
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @param locale - Optional locale override
 * @returns Formatted date string
 */
export function formatDate(
  date: Date | string,
  options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' },
  locale?: string
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const dateLocale = locale || getDateLocale();
  return dateObj.toLocaleDateString(dateLocale, options);
}

/**
 * Common date format presets for convenience
 */
export const DateFormats = {
  short: { month: 'short', day: 'numeric', year: 'numeric' } as const,
  medium: { weekday: 'short', month: 'short', day: 'numeric' } as const,
  long: { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' } as const,
  monthDay: { month: 'short', day: 'numeric' } as const,
  full: { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' } as const,
};

/** Age in whole years from a birthday string, or null when missing/implausible. */
export function getAge(birthday?: string | null): number | null {
  if (!birthday) return null;
  const d = new Date(birthday);
  if (isNaN(d.getTime())) return null;
  const age = Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 3600 * 1000));
  return age > 0 && age < 120 ? age : null;
}
